/**
 * One structured ops snapshot for a later Grok pass and the offline scorer.
 * Pure. Same keys every time. Reads board state; does not mint or post.
 */
import { dutyViewFor, type DutyRoster } from "./duty";
import {
  hydrateExperienceLog,
  type ExperienceRecord,
} from "./experienceLog";
import { isCharterAssignable } from "./maintenance";
import {
  hydrateCostLines,
  hydrateCrewLedger,
  type CostLineCategory,
  type CostLineItem,
  type CrewLedgerEntry,
  type OpsMoneyCode,
} from "./opsLedger";
import {
  effectiveAircraftCostTypes,
  resolveAircraftOperatingCost,
  resolveCustomerAircraftRequest,
} from "./aircraftOperatingCost";
import { opsSettingsSlice, resolveCrewComplement, type OpsSettingsSlice } from "./opsDeskSettings";
import { resolveAircraftFuelUnit } from "./fuelUnits";
import type {
  AircraftSpec,
  CharterRequest,
  CompanyAccount,
  CompanyProfile,
  CrewMember,
  LedgerTxn,
  LedgerTxnKind,
  MelItem,
  Mission,
  OpsAircraftHourlyCost,
  SceneryAirport,
  Squawk,
} from "./types";

export const OPS_SNAPSHOT_KEYS = [
  "generatedAtUtc",
  "horizonDays",
  "settings",
  "aircraftCosts",
  "fleet",
  "crew",
  "inbox",
  "trips",
  "costLedger",
  "crewLedger",
  "detect",
  "weather",
  "sharedTails",
  "recentOutcomes",
] as const;

const TRIP_CAP = 30;
const INBOX_CAP = 24;
const OUTCOME_CAP = 12;
const LEDGER_CAP = 12;
const DETECT_CAP = 48;
const LOOKBACK_MS = 6 * 60 * 60_000;

export type OpsWeatherFlag = {
  kind: "note" | "deice" | "icing";
  text: string;
  missionId?: string;
  icao?: string;
};

export type SharedTailLockHint = {
  aircraftId: string;
  registration?: string;
  holder?: string;
  note?: string;
};

export type OpsFleetRow = {
  id: string;
  registration: string;
  icaoType: string;
  status: string;
  assignable: boolean;
  lastAirportIcao: string;
  baseIcao: string;
  fuelUnit: string;
  fuelOnBoardGal: number | null;
  singlePilot: boolean;
  crewComplement: "dual" | "single";
};

export type OpsCrewRow = {
  id: string;
  name: string;
  dutyStatus: string;
  dutyLabel: string;
  fdpRemainMin: number | null;
  flightRemainMin: number | null;
  ratedIcaoTypes: string[];
};

export type OpsInboxRow = {
  id: string;
  originIcao: string;
  destIcao: string;
  departureIso: string;
  paxCount: number;
  flightType: string;
  status: string;
  suggestedAircraftId: string;
  /** Resolved customer type. Empty when the RFQ did not name one. */
  requestedIcaoType: string;
  /** Resolved company class label. Empty when the RFQ did not name one. */
  requestedClass: string;
};

export type OpsAircraftCostTable = {
  types: OpsAircraftHourlyCost[];
  tails: ReturnType<typeof resolveAircraftOperatingCost>[];
};

export type OpsTripRow = {
  id: string;
  flightNumber: string;
  flightType: string;
  originIcao: string;
  destIcao: string;
  departureIso: string;
  status: string;
  registration: string;
  aircraftId: string;
  paxCount: number;
};

export type OpsOutcomeRow = {
  atUtc: string;
  action: "accept" | "decline";
  requestId: string;
  missionId: string;
  originIcao: string;
  destIcao: string;
  flightType: string;
  outcome: string;
};

export type OpsSnapshot = {
  generatedAtUtc: string;
  horizonDays: number;
  settings: OpsSettingsSlice;
  /** Company hourly-cost table plus the resolved number on each tail. */
  aircraftCosts: OpsAircraftCostTable;
  fleet: OpsFleetRow[];
  crew: OpsCrewRow[];
  inbox: OpsInboxRow[];
  trips: OpsTripRow[];
  costLedger: { posted: boolean; lines: CostLineItem[] };
  crewLedger: { posted: boolean; entries: CrewLedgerEntry[] };
  detect: {
    inventoryIcaos: string[];
    inventoryCount: number;
    destinationIcaos: string[];
    destinationCount: number;
  };
  weather: { flags: OpsWeatherFlag[] };
  sharedTails: { locks: SharedTailLockHint[] };
  recentOutcomes: OpsOutcomeRow[];
};

export type OpsSnapshotInput = {
  now?: Date;
  company?: Partial<CompanyProfile> | null;
  fleet?: readonly AircraftSpec[];
  crew?: readonly CrewMember[];
  duty?: DutyRoster | null;
  inbox?: readonly CharterRequest[];
  missions?: readonly Mission[];
  scenery?: readonly SceneryAirport[];
  account?: CompanyAccount | null;
  squawks?: readonly Squawk[];
  melItems?: readonly MelItem[];
  /** Present (even empty) means the cost-line slice has been posted. */
  costLines?: readonly CostLineItem[] | null;
  crewLedger?: readonly CrewLedgerEntry[] | null;
  experienceLog?: readonly ExperienceRecord[] | null;
  weatherFlags?: readonly OpsWeatherFlag[] | null;
  sharedTailLocks?: readonly SharedTailLockHint[] | null;
};

function icaoList(values: readonly string[], cap: number): { icaos: string[]; count: number } {
  const seen = new Set<string>();
  for (const raw of values) {
    const code = String(raw ?? "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
    if (code.length < 3) continue;
    seen.add(code.slice(0, 4));
  }
  const all = [...seen].sort();
  return { icaos: all.slice(0, cap), count: all.length };
}

function inHorizon(iso: string, nowMs: number, days: number): boolean {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  return t >= nowMs - LOOKBACK_MS && t <= nowMs + days * 24 * 60 * 60_000;
}

function weatherKind(text: string): OpsWeatherFlag["kind"] {
  if (/de-?ice|anti-?ice/i.test(text)) return "deice";
  if (/\bicing\b|\bice\b|FZRA|FZDZ/i.test(text)) return "icing";
  return "note";
}

function hydrateWeatherFlags(raw: readonly OpsWeatherFlag[] | null | undefined): OpsWeatherFlag[] {
  if (!raw?.length) return [];
  const out: OpsWeatherFlag[] = [];
  for (const row of raw) {
    if (!row || (row.kind !== "note" && row.kind !== "deice" && row.kind !== "icing")) continue;
    const text = String(row.text ?? "").trim().slice(0, 160);
    if (!text) continue;
    const missionId = String(row.missionId ?? "").trim().slice(0, 64);
    const icao = String(row.icao ?? "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 4);
    out.push({
      kind: row.kind,
      text,
      ...(missionId ? { missionId } : {}),
      ...(icao ? { icao } : {}),
    });
  }
  return out.slice(0, 24);
}

function flagsFromMissions(missions: readonly Mission[]): OpsWeatherFlag[] {
  const out: OpsWeatherFlag[] = [];
  for (const mission of missions) {
    const text = String(mission.weatherNote ?? "").trim().slice(0, 160);
    if (!text) continue;
    out.push({ kind: weatherKind(text), text, missionId: mission.id });
    if (out.length >= 12) break;
  }
  return out;
}

function hydrateLocks(raw: readonly SharedTailLockHint[] | null | undefined): SharedTailLockHint[] {
  if (!raw?.length) return [];
  const out: SharedTailLockHint[] = [];
  for (const row of raw) {
    const aircraftId = String(row?.aircraftId ?? "").trim().slice(0, 64);
    if (!aircraftId) continue;
    const registration = String(row.registration ?? "").trim().slice(0, 16);
    const holder = String(row.holder ?? "").trim().slice(0, 64);
    const note = String(row.note ?? "").trim().slice(0, 120);
    out.push({
      aircraftId,
      ...(registration ? { registration } : {}),
      ...(holder ? { holder } : {}),
      ...(note ? { note } : {}),
    });
  }
  return out.slice(0, 24);
}

const ACCOUNT_COST_KIND: Partial<Record<LedgerTxnKind, CostLineCategory>> = {
  fuel: "fuel",
  landing: "landing",
  handling: "handling",
  hotel: "other",
  parking: "other",
  mx_labor: "mx_insurance",
  mx_parts: "mx_insurance",
  mx_inspection: "mx_insurance",
  mx_reserve: "mx_insurance",
  engine_reserve: "mx_insurance",
};

function moneyCode(raw: unknown): OpsMoneyCode {
  return raw === "USD" || raw === "EUR" || raw === "GBP" || raw === "CAD" ? raw : "CAD";
}

/** Read stub from the company account when no cost-line slice has been posted. */
export function costLinesFromAccount(account: CompanyAccount | null | undefined): CostLineItem[] {
  const txns = account?.transactions ?? [];
  const lines: CostLineItem[] = [];
  for (const txn of txns) {
    const category = ACCOUNT_COST_KIND[txn.kind];
    if (!category) continue;
    const line = accountTxnToCostLine(txn, category);
    if (line) lines.push(line);
    if (lines.length >= LEDGER_CAP) break;
  }
  return lines;
}

function accountTxnToCostLine(txn: LedgerTxn, category: CostLineCategory): CostLineItem | null {
  if (!txn.id || typeof txn.amountCad !== "number" || !Number.isFinite(txn.amountCad)) return null;
  const missionId = txn.missionId?.trim() ?? "";
  const memo = txn.memo?.trim().slice(0, 120) ?? "";
  return {
    id: `acct-${txn.id}`.slice(0, 64),
    category,
    amount: Math.round(txn.amountCad * 100) / 100,
    currency: moneyCode(txn.currency),
    createdAtUtc: txn.createdAtUtc || "",
    ...(missionId ? { missionId } : {}),
    ...(txn.aircraftId ? { aircraftId: txn.aircraftId } : {}),
    ...(memo ? { memo } : {}),
  };
}

function inboxRow(row: CharterRequest, settings: OpsSettingsSlice): OpsInboxRow {
  const ask = resolveCustomerAircraftRequest(row, settings);
  return {
    id: row.id,
    originIcao: row.originIcao,
    destIcao: row.destIcao,
    departureIso: row.departureIso,
    paxCount: row.paxCount,
    flightType: row.flightType,
    status: row.status,
    suggestedAircraftId: row.suggestedAircraftId,
    requestedIcaoType: ask.icaoType,
    requestedClass: ask.classLabel,
  };
}

function tripRow(mission: Mission): OpsTripRow {
  return {
    id: mission.id,
    flightNumber: mission.flightNumber ?? mission.callsign ?? "",
    flightType: mission.flightType,
    originIcao: mission.originIcao,
    destIcao: mission.destIcao,
    departureIso: mission.departureIso,
    status: mission.status,
    registration: mission.registration,
    aircraftId: mission.aircraftId,
    paxCount: mission.paxCount,
  };
}

function outcomeFromInbox(row: CharterRequest): OpsOutcomeRow | null {
  if (row.status !== "accepted" && row.status !== "declined") return null;
  return {
    atUtc: row.createdAt,
    action: row.status === "accepted" ? "accept" : "decline",
    requestId: row.id,
    missionId: row.acceptedMissionId ?? "",
    originIcao: row.originIcao,
    destIcao: row.destIcao,
    flightType: row.flightType,
    outcome: row.status,
  };
}

function outcomeFromExperience(record: ExperienceRecord): OpsOutcomeRow | null {
  if (record.action !== "accept" && record.action !== "decline") return null;
  return {
    atUtc: record.atUtc,
    action: record.action,
    requestId: record.requestId ?? "",
    missionId: record.missionId ?? "",
    originIcao: record.features.originIcao,
    destIcao: record.features.destIcao,
    flightType: record.features.flightType,
    outcome: record.outcome,
  };
}

/**
 * Desk consumer. The stored crew ledger is passed even when empty so
 * `crewLedger.posted` is true. Cost lines stay on the account stub until
 * this desk has posted at least one `CostLineItem` — pass `costLines` then.
 */
export function buildDeskOpsSnapshot(input: OpsSnapshotInput = {}): OpsSnapshot {
  return buildOpsSnapshot({
    ...input,
    crewLedger: input.crewLedger ?? [],
  });
}

export function buildOpsSnapshot(input: OpsSnapshotInput = {}): OpsSnapshot {
  const now = input.now ?? new Date();
  const settings = opsSettingsSlice(input.company);
  const horizonDays = settings.rfqHorizonDays;
  const nowMs = now.getTime();
  const squawks = input.squawks ?? [];
  const melItems = input.melItems ?? [];
  const fleet = input.fleet ?? [];
  const missions = input.missions ?? [];
  const inbox = input.inbox ?? [];

  const fleetRows: OpsFleetRow[] = fleet.map((tail) => ({
    id: tail.id,
    registration: tail.registration,
    icaoType: tail.icaoType,
    status: tail.status,
    assignable: isCharterAssignable(tail, [...squawks], [...melItems]),
    lastAirportIcao: tail.lastAirportIcao ?? "",
    baseIcao: tail.baseIcao ?? "",
    fuelUnit: resolveAircraftFuelUnit(tail, settings.defaultFuelUnit),
    fuelOnBoardGal: typeof tail.fuelOnBoardGal === "number" && Number.isFinite(tail.fuelOnBoardGal) ? tail.fuelOnBoardGal : null,
    singlePilot: tail.singlePilot === true,
    crewComplement: resolveCrewComplement(settings, tail.singlePilot),
  }));

  const duty = input.duty ?? null;
  const crewRows: OpsCrewRow[] = (input.crew ?? []).map((member) => {
    if (!duty) {
      return {
        id: member.id,
        name: member.displayName,
        dutyStatus: "unknown",
        dutyLabel: "",
        fdpRemainMin: null,
        flightRemainMin: null,
        ratedIcaoTypes: [...(member.ratedIcaoTypes ?? [])],
      };
    }
    const view = dutyViewFor(duty, { id: member.id, name: member.displayName }, now);
    return {
      id: member.id,
      name: member.displayName,
      dutyStatus: view.status,
      dutyLabel: view.statusLabel,
      fdpRemainMin: view.fdpRemainMin,
      flightRemainMin: view.flightRemainMin,
      ratedIcaoTypes: [...(member.ratedIcaoTypes ?? [])],
    };
  });

  const openInbox = inbox
    .filter((row) => row.status === "new" || row.status === "assigned")
    .slice()
    .sort((a, b) => a.departureIso.localeCompare(b.departureIso))
    .slice(0, INBOX_CAP)
    .map((row) => inboxRow(row, settings));

  const trips = missions
    .filter((mission) => mission.status === "in-progress" || inHorizon(mission.departureIso, nowMs, horizonDays))
    .slice()
    .sort((a, b) => a.departureIso.localeCompare(b.departureIso))
    .slice(0, TRIP_CAP)
    .map(tripRow);

  const costPosted = input.costLines != null;
  const costLines = costPosted ? hydrateCostLines(input.costLines).slice(0, LEDGER_CAP) : costLinesFromAccount(input.account);
  const crewPosted = input.crewLedger != null;
  const crewEntries = crewPosted ? hydrateCrewLedger(input.crewLedger).slice(0, LEDGER_CAP) : [];

  const inventory = icaoList(
    (input.crew ?? []).flatMap((member) => (member.sceneryInventory ?? []).map((row) => row.icao)),
    DETECT_CAP,
  );
  const destinations = icaoList((input.scenery ?? []).map((row) => row.icao), DETECT_CAP);

  const weatherFlags = [...hydrateWeatherFlags(input.weatherFlags), ...flagsFromMissions(missions)].slice(0, 24);

  const outcomes: OpsOutcomeRow[] = [];
  const seen = new Set<string>();
  for (const record of hydrateExperienceLog(input.experienceLog)) {
    const row = outcomeFromExperience(record);
    if (!row) continue;
    const key = `${row.action}:${row.requestId || record.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    outcomes.push(row);
  }
  for (const row of inbox) {
    const outcome = outcomeFromInbox(row);
    if (!outcome) continue;
    const key = `${outcome.action}:${outcome.requestId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    outcomes.push(outcome);
  }
  outcomes.sort((a, b) => b.atUtc.localeCompare(a.atUtc));

  return {
    generatedAtUtc: now.toISOString(),
    horizonDays,
    settings,
    aircraftCosts: {
      types: effectiveAircraftCostTypes(
        settings,
        fleet.map((tail) => tail.icaoType),
      ),
      tails: fleet.map((tail) => resolveAircraftOperatingCost(tail, settings)),
    },
    fleet: fleetRows,
    crew: crewRows,
    inbox: openInbox,
    trips,
    costLedger: { posted: costPosted, lines: costLines },
    crewLedger: { posted: crewPosted, entries: crewEntries },
    detect: {
      inventoryIcaos: inventory.icaos,
      inventoryCount: inventory.count,
      destinationIcaos: destinations.icaos,
      destinationCount: destinations.count,
    },
    weather: { flags: weatherFlags },
    sharedTails: { locks: hydrateLocks(input.sharedTailLocks) },
    recentOutcomes: outcomes.slice(0, OUTCOME_CAP),
  };
}
